import { useEffect, useMemo, useRef, useState } from "react";
import {
  ITEM_SORTS,
  ITEM_STATUSES,
  PHOTO_FILTERS,
  PHOTO_FILTER_LABELS,
  SORT_LABELS,
  STATUS_LABELS,
  STOCK_FILTERS,
  STOCK_FILTER_LABELS,
  flattenLocations,
  normalisePhotoFilter,
  normaliseSort,
  normaliseStatus,
  normaliseStockFilter,
  parseScan,
  pathLabel,
  validId,
  type Client,
  type ItemQuery,
  type ItemSort,
  type ItemStatus,
  type ItemSummary,
  type PhotoFilter,
  atClient,
  type ClientColor,
  type StockOverview,
  type SavedSearch,
  type ScanTarget,
  type SessionUser,
  type StockFilter,
  formatCount,
} from "@iltocat/shared";
import { ApiError, api, plural, storageWarning } from "../lib/api.js";
import { captureStats, readableTime } from "../lib/capture.js";
import { readRecentSearches, rememberSearch } from "../lib/recentSearches.js";
import { onSearchFocus } from "../lib/searchFocus.js";
import { useItemView } from "../lib/useItemView.js";
import { useItems } from "../lib/useItems.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { clientColors } from "../components/ClientTag.js";
import { EmptyState } from "../components/EmptyState.js";
import { BulkBar } from "../components/BulkBar.js";
import { FilterChip, FilterToggle } from "../components/FilterChip.js";
import { LoadMore } from "../components/LoadMore.js";
import { Icon } from "../components/Icon.js";
import { ItemCsvExport } from "../components/ItemCsvExport.js";
import { hasCamera } from "../components/CameraView.js";
import { ScanSearchDialog } from "../components/ScanSearchDialog.js";
import { ItemGrid, ItemViewToggle, type StockTone } from "../components/ItemGrid.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { useToast } from "../components/Toast.js";

/**
 * Everything you own, and the one box that finds any of it.
 *
 * Until v2 this was two screens: home (three piles and a category filter) and
 * search (text, five filters, saved searches). Both were a grid of items with
 * ways to narrow it, and search with nothing typed was already "everything,
 * newest first", so they are one screen now. The piles were tabs under the
 * box until 2026-09-24, when "Needs details" and "Not filed" joined the other
 * filters: both are niche, and three tabs took a row for them
 * (docs/notes/decisions.md). In a stockroom the filters are about stock --
 * on the shelf, held, out at which client -- instead of Keep and For sale.
 * Since 2026-09-24 each filter is a chip in a row under the box (FilterChip)
 * rather than a select in a panel behind a Filters button, and retired stock
 * is left out unless the Stock chip asks for it.
 *
 * **The whole state is in the address.** Text, piles, filters and sort are the
 * query string, rewritten as they change (with `replaceState`, so typing does
 * not fill the back button with one entry per letter). That is what makes the
 * back button from an item land on the same results, makes a search a
 * bookmark, and lets a saved search be nothing more than a stored query string.
 * An empty state keeps whichever path it arrived on, `/` or `/search`.
 */

interface State {
  q: string;
  /** Only items still missing a title: the pile a fast capture session leaves. */
  needs: boolean;
  photos: PhotoFilter | undefined;
  /** `undefined` did not ask, `null` is "none", a string is one. Same for location. */
  category: string | null | undefined;
  location: string | null | undefined;
  tag: string | undefined;
  status: ItemStatus | undefined;
  /** A stockroom's: where it is as stock, and which client it is out at. */
  stock: StockFilter | undefined;
  client: string | undefined;
  sort: ItemSort | undefined;
}

const EMPTY: State = {
  q: "",
  needs: false,
  photos: undefined,
  category: undefined,
  location: undefined,
  tag: undefined,
  status: undefined,
  stock: undefined,
  client: undefined,
  sort: undefined,
};

/** The three-way values (`none` is "nothing filed") shared with the API's query string. */
const idOrNone = (value: string | null): string | null | undefined =>
  value === "none" ? null : validId(value) ? value : undefined;

function parse(search: string): State {
  const params = new URLSearchParams(search);
  const status = params.get("status");
  return {
    q: (params.get("q") ?? "").slice(0, 200),
    needs: params.get("needs") === "1",
    photos: normalisePhotoFilter(params.get("photo")),
    category: idOrNone(params.get("category")),
    location: idOrNone(params.get("location")),
    tag: validId(params.get("tag")) ? (params.get("tag") ?? undefined) : undefined,
    status: status ? normaliseStatus(status) : undefined,
    stock: normaliseStockFilter(params.get("stock")),
    client: validId(params.get("client")) ? (params.get("client") ?? undefined) : undefined,
    sort: normaliseSort(params.get("sort")),
  };
}

function serialise(state: State): string {
  const params = new URLSearchParams();
  if (state.q.trim()) params.set("q", state.q.trim());
  if (state.needs) params.set("needs", "1");
  if (state.photos) params.set("photo", state.photos);
  if (state.category !== undefined) params.set("category", state.category ?? "none");
  if (state.location !== undefined) params.set("location", state.location ?? "none");
  if (state.tag) params.set("tag", state.tag);
  if (state.status) params.set("status", state.status);
  if (state.stock) params.set("stock", state.stock);
  if (state.client) params.set("client", state.client);
  if (state.sort) params.set("sort", state.sort);
  return params.toString();
}

const isActive = (state: State): boolean => serialise(state) !== "";

/** Only the keys the API knows, and only when they mean something. `relevance` is meaningless without text. */
function toQuery(state: State): ItemQuery {
  const query: ItemQuery = {};
  if (state.q.trim()) query.q = state.q.trim();
  if (state.needs) query.needsDetails = true;
  if (state.photos) query.photos = state.photos;
  if (state.category !== undefined) query.categoryId = state.category;
  if (state.location !== undefined) {
    query.locationId = state.location;
    // "Basement" means the basement and everything in it. Only the shelf view
    // is about what is filed directly in one place.
    if (state.location !== null) query.deepLocation = true;
  }
  if (state.tag) query.tagId = state.tag;
  if (state.status) query.status = state.status;
  if (state.stock) query.stock = state.stock;
  if (state.client) query.clientId = state.client;
  if (state.sort && !(state.sort === "relevance" && !state.q.trim())) query.sort = state.sort;
  return query;
}

type StockLabel = { label: string; tone: StockTone; color?: ClientColor | null };

const NOT_STOCK: StockLabel = { label: "Not on the shelf", tone: "none" };

/** What each card in a stockroom says about its item, from the same overview the Stock screen reads. */
function stockLabelsFrom(
  overview: StockOverview,
  colors: Map<string, ClientColor | null>,
): Map<string, StockLabel> {
  const labels = new Map<string, StockLabel>();
  for (const row of overview.retired) labels.set(row.itemId, { label: "Retired", tone: "retired" });
  for (const row of overview.items) {
    const held = row.holds.reduce((sum, hold) => sum + hold.quantity, 0);
    // A bulk line with every unit retired is retired, not "0 on the shelf".
    if (row.tracking === "bulk" && row.onHand === 0 && row.deployed === 0 && labels.has(row.itemId)) continue;
    if (row.tracking === "bulk") {
      labels.set(row.itemId, {
        label: `${formatCount(row.onHand)} on the shelf${held > 0 ? ` · ${formatCount(held)} held` : ""}${row.deployed > 0 ? ` · ${formatCount(row.deployed)} out` : ""}`,
        tone: row.onHand > 0 ? "shelf" : "out",
      });
    } else if (row.state === "deployed") {
      const line = overview.open.find((open) => open.itemId === row.itemId);
      labels.set(
        row.itemId,
        line
          ? {
              label: `At ${atClient(line.clientName, line.spot)}`,
              tone: "out",
              color: colors.get(line.clientId) ?? null,
            }
          : { label: "Deployed", tone: "out" },
      );
    } else if (held > 0) {
      // On the shelf, but spoken for: the reserve's indicator, where the grid shows it.
      const names = row.holds.map((hold) => hold.clientName).join(", ");
      // One client's colour when it is held for one client; mixed holds stay the reserve violet.
      const owners = new Set(row.holds.map((hold) => hold.clientId));
      const only = owners.size === 1 ? row.holds[0]?.clientId : undefined;
      labels.set(row.itemId, {
        label: `Held for ${names}`,
        tone: "held",
        color: only ? (colors.get(only) ?? null) : null,
      });
    } else {
      labels.set(row.itemId, { label: "On the shelf", tone: "shelf" });
    }
  }
  return labels;
}

export function Library({
  user,
  taxonomy,
  vocabulary,
  onOpen,
  onCapture,
  onSort,
  onLabels,
  onScanLabel,
}: {
  user: SessionUser;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onOpen: (id: string) => void;
  onCapture: () => void;
  /** Filing the unfiled pile one at a time. */
  onSort: () => void;
  /** Labels or cards for the selection, one print run. */
  onLabels: (ids: string[]) => void;
  /** One of our own labels, read by the search box's camera: open what it labels. */
  onScanLabel: (target: Exclude<ScanTarget, { kind: "text" }>) => void;
}) {
  const [state, setState] = useState<State>(() => parse(window.location.search));
  const [text, setText] = useState(state.q);
  const [saved, setSaved] = useState<SavedSearch[]>([]);
  const [recent, setRecent] = useState<string[]>(readRecentSearches);
  const [naming, setNaming] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [view, chooseView] = useItemView(user.activeKind === "stock");
  const searchBox = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => onSearchFocus(() => searchBox.current?.focus()), []);

  // Typing is applied a beat after the last key: a query per letter would be a
  // round trip per letter, and results that flicker while somebody is still
  // deciding what to type are worse than a short wait.
  useEffect(() => {
    if (text === state.q) return;
    const timer = window.setTimeout(() => setState((current) => ({ ...current, q: text })), 250);
    return () => window.clearTimeout(timer);
  }, [text, state.q]);

  useEffect(() => {
    const search = serialise(state);
    const base = window.location.pathname.startsWith("/search") || search ? "/search" : "/";
    window.history.replaceState({}, "", search ? `${base}?${search}` : base);
  }, [state]);

  useEffect(() => {
    void api.searches
      .list()
      .then(({ searches }) => setSaved(searches))
      .catch(() => undefined);
  }, []);

  const query = useMemo(() => toQuery(state), [state]);

  // In a stockroom each card says where the thing is as stock. One overview
  // for the whole grid: a stockroom is hundreds of rows, not a page per card.
  // Read again after the bulk bar retires or moves something.
  const [overview, setOverview] = useState<StockOverview | null>(null);
  const [stockRead, setStockRead] = useState(0);
  useEffect(() => {
    if (user.activeKind !== "stock") return;
    void api.stock
      .overview()
      .then(setOverview)
      .catch(() => undefined);
  }, [user.activeKind, user.activeInventoryId, stockRead]);
  const list = useItems(query);
  const stockroom = user.activeKind === "stock";
  const active = isActive(state);
  const searching = state.q.trim() !== "";
  const filterCount =
    Number(state.needs) +
    Number(state.photos !== undefined) +
    Number(state.category !== undefined) +
    Number(state.tag !== undefined) +
    Number(state.status !== undefined) +
    Number(state.location !== undefined) +
    Number(state.stock !== undefined) +
    Number(state.client !== undefined);

  // A stockroom's client filter needs the client list; a personal inventory has none.
  const [clients, setClients] = useState<Client[]>([]);
  useEffect(() => {
    if (user.activeKind !== "stock") return;
    void api.clients
      .list()
      .then(({ clients: list }) => setClients(list))
      .catch(() => undefined);
  }, [user.activeKind, user.activeInventoryId]);
  const stockLabels = useMemo(
    () => (overview ? stockLabelsFrom(overview, clientColors(clients)) : null),
    [overview, clients],
  );

  const locationOptions = useMemo(
    () => flattenLocations(taxonomy.tree).map((node) => ({ id: node.id, label: pathLabel(node.path) })),
    [taxonomy.tree],
  );

  const locationOf = (item: ItemSummary): string => {
    const node = item.locationId ? taxonomy.byId.get(item.locationId) : undefined;
    return node ? pathLabel(node.path) : "";
  };

  const toggle = (id: string) =>
    setPicked((now) => {
      const next = new Set(now);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const stopSelecting = () => {
    setSelecting(false);
    setPicked(new Set());
  };

  // A new search or filter is a new set of items. Keeping ticks on ones no
  // longer shown would have Apply change things nobody can see.
  const searchKey = serialise(state);
  useEffect(() => setPicked(new Set()), [searchKey]);

  useEffect(() => {
    if (!selecting) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") stopSelecting();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selecting]);

  const set = (changes: Partial<State>) => setState((current) => ({ ...current, ...changes }));

  const categoryName = (id: string | null) =>
    id === null
      ? "No category"
      : (taxonomy.categories.find((category) => category.id === id)?.name ?? "A category");
  const tagName = (id: string) => vocabulary.tags.find((tag) => tag.id === id)?.name ?? "tag";
  const placeName = (id: string) => locationOptions.find((option) => option.id === id)?.label ?? "A place";
  const clientName = (id: string) => clients.find((client) => client.id === id)?.name ?? "a client";

  /** A saved or recent search, as words: what it was looking for and what it was narrowed by. */
  const describe = (search: string): string => {
    const found = parse(search);
    const parts: string[] = [];
    if (found.q) parts.push(`“${found.q}”`);
    if (found.needs) parts.push("needs details");
    if (found.photos) parts.push(PHOTO_FILTER_LABELS[found.photos].toLowerCase());
    if (found.category !== undefined) parts.push(categoryName(found.category));
    if (found.tag) parts.push(`#${tagName(found.tag)}`);
    if (found.status) parts.push(STATUS_LABELS[found.status]);
    if (found.location !== undefined)
      parts.push(found.location === null ? "not filed" : placeName(found.location));
    if (found.stock) parts.push(STOCK_FILTER_LABELS[found.stock].toLowerCase());
    if (found.client) parts.push(`at ${clientName(found.client)}`);
    return parts.join(" · ") || "Everything";
  };

  const apply = (search: string) => {
    const next = parse(search);
    setState(next);
    setText(next.q);
    setNaming(null);
  };

  const clear = () => {
    setState(EMPTY);
    setText("");
    setNaming(null);
  };

  const remember = () => {
    if (active) setRecent(rememberSearch(serialise(state)));
  };

  async function save() {
    const name = (naming ?? "").trim();
    if (!name) return;
    setProblem(null);
    try {
      setSaved((await api.searches.save(name, serialise(state))).searches);
      setNaming(null);
      toast("Search saved.");
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That search did not save.");
    }
  }

  async function forget(id: string) {
    try {
      setSaved((await api.searches.remove(id)).searches);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That did not delete.");
    }
  }

  // Read on render rather than kept in state: it changes only when a capture
  // finishes, and a capture finishing is a navigation back to this screen.
  const stats = captureStats();
  const storage = storageWarning(user.usedBytes, user.quotaBytes);

  const count = list.loading ? "" : active ? plural(list.total, "result") : plural(list.total, "item");

  return (
    <>
      <PageHeader
        title="Items"
        meta={
          <>
            {count && <span>{count}</span>}
            {stats.thisSession > 0 && (
              <span
                className="pill pill-accent"
                title={
                  stats.fastestMs !== null
                    ? `Quickest on this device: ${readableTime(stats.fastestMs)}`
                    : undefined
                }
              >
                <Icon name="zap" size={13} />
                {plural(stats.thisSession, "item")} this session
                {stats.medianMs !== null ? `, ~${readableTime(stats.medianMs)} each` : ""}
              </span>
            )}
          </>
        }
      />

      {storage && <Notice tone={storage.tone}>{storage.message}</Notice>}

      <div className="toolbar">
        <form
          className="searchbox"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            setState((current) => ({ ...current, q: text }));
            if (text.trim() || active) setRecent(rememberSearch(serialise({ ...state, q: text })));
          }}
        >
          <Icon name="search" size={18} />
          <input
            ref={searchBox}
            type="search"
            aria-label="Search your things"
            placeholder="Search a name, serial number, shelf or note"
            enterKeyHint="search"
            autoComplete="off"
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          {text && (
            <button
              type="button"
              className="icon-button icon-button-sm"
              aria-label="Clear the search text"
              onClick={() => {
                setText("");
                set({ q: "" });
                searchBox.current?.focus();
              }}
            >
              <Icon name="x" size={16} />
            </button>
          )}
          {hasCamera() && (
            <button
              type="button"
              className="icon-button icon-button-sm"
              aria-label="Scan a barcode to search for it"
              title="Scan a barcode to search for it"
              onClick={() => setScanning(true)}
            >
              <Icon name="camera" size={18} />
            </button>
          )}
        </form>
      </div>

      {scanning && (
        <ScanSearchDialog
          onClose={() => setScanning(false)}
          onRead={(raw) => {
            const target = parseScan(raw);
            if (!target) return;
            if (target.kind !== "text") return onScanLabel(target);
            setText(target.text);
            set({ q: target.text });
          }}
        />
      )}

      <div className="filter-bar" role="group" aria-label="Filters">
        {stockroom && (
          <>
            <FilterChip<StockFilter>
              label="Stock"
              value={state.stock}
              anyLabel="Anything in service"
              options={STOCK_FILTERS.map((stock) => ({ value: stock, label: STOCK_FILTER_LABELS[stock] }))}
              onChange={(stock) => set({ stock })}
            />
            {clients.length > 0 && (
              <FilterChip
                label="Client"
                value={state.client}
                anyLabel="Any client, or none"
                options={clients.map((client) => ({ value: client.id, label: `At ${client.name}` }))}
                onChange={(client) => set({ client })}
              />
            )}
          </>
        )}
        <FilterChip
          label="Category"
          value={state.category === undefined ? undefined : (state.category ?? "none")}
          options={[
            { value: "none", label: "No category" },
            ...taxonomy.categories.map((category) => ({
              value: category.id,
              label: category.name,
              count: category.itemCount,
            })),
          ]}
          onChange={(next) => set({ category: next === undefined ? undefined : idOrNone(next) })}
        />
        {vocabulary.tags.length > 0 && (
          <FilterChip
            label="Tag"
            value={state.tag}
            options={vocabulary.tags.map((tag) => ({
              value: tag.id,
              label: `#${tag.name}`,
              count: tag.itemCount,
            }))}
            onChange={(tag) => set({ tag })}
          />
        )}
        <FilterChip
          label="Location"
          value={state.location === undefined ? undefined : (state.location ?? "none")}
          anyLabel="Anywhere"
          options={[
            { value: "none", label: "Not filed anywhere" },
            ...locationOptions.map((option) => ({ value: option.id, label: option.label })),
          ]}
          onChange={(next) => set({ location: next === undefined ? undefined : idOrNone(next) })}
        />
        {!stockroom && (
          <FilterChip<ItemStatus>
            label="Status"
            value={state.status}
            options={ITEM_STATUSES.map((status) => ({ value: status, label: STATUS_LABELS[status] }))}
            onChange={(status) => set({ status })}
          />
        )}
        <FilterChip<PhotoFilter>
          label="Photo"
          value={state.photos}
          anyLabel="With or without"
          options={PHOTO_FILTERS.map((photos) => ({ value: photos, label: PHOTO_FILTER_LABELS[photos] }))}
          onChange={(photos) => set({ photos })}
        />
        <FilterToggle label="Needs a name" on={state.needs} onChange={(needs) => set({ needs })} />
      </div>

      <div className="toolbar-row">
        <label className="sort">
          <span className="sr-only">Sort</span>
          <select
            className="input input-small"
            value={state.sort ?? (searching ? "relevance" : "newest")}
            onChange={(event) => set({ sort: normaliseSort(event.target.value) })}
          >
            {ITEM_SORTS.filter((sort) => sort !== "relevance" || searching).map((sort) => (
              <option key={sort} value={sort}>
                {SORT_LABELS[sort]}
              </option>
            ))}
          </select>
        </label>
        {state.location === null && list.total > 0 && !selecting && (
          <button type="button" className="button button-quiet button-small" onClick={onSort}>
            <Icon name="zap" size={16} />
            Sort these
          </button>
        )}
        <span className="toolbar-gap" />
        <ItemViewToggle value={view} onChange={chooseView} />
        {(selecting || list.items.length > 0) && (
          <button
            type="button"
            className={
              selecting ? "button button-quiet button-small button-on" : "button button-quiet button-small"
            }
            aria-pressed={selecting}
            aria-label={selecting ? "Done selecting" : "Select"}
            onClick={() => (selecting ? stopSelecting() : setSelecting(true))}
          >
            <Icon name="check-circle" size={16} />
            {/* "Done" stays in words: it is the way out of a mode, not a shortcut into one. */}
            {selecting ? "Done" : <span className="hide-sm">Select</span>}
          </button>
        )}
        {list.items.length > 0 && (
          <button
            type="button"
            className={
              exporting ? "button button-quiet button-small button-on" : "button button-quiet button-small"
            }
            aria-expanded={exporting}
            aria-label={active ? "Export these" : "Export"}
            onClick={() => setExporting((open) => !open)}
          >
            <Icon name="download" size={16} />
            <span className="hide-sm">{active ? "Export these" : "Export"}</span>
          </button>
        )}
      </div>

      {exporting && (
        <div className="csv-export">
          <ItemCsvExport
            key={serialise(state)}
            inventoryId={user.activeInventoryId}
            taxonomy={taxonomy}
            query={query}
            stock={user.activeKind === "stock"}
            title={active ? "Export these results" : "Export as a CSV"}
            note={
              active
                ? `The ${plural(list.total, "item")} matching this search, in the columns you pick. Clear the search to export everything.`
                : "Every item, in the columns you pick. Search or filter first to export only some."
            }
          />
        </div>
      )}

      {(active || saved.length > 0 || recent.length > 0) && (
        <div className="chip-bar">
          {active && naming === null && (
            <>
              <button type="button" className="chip chip-ghost" onClick={() => setNaming(state.q.trim())}>
                <Icon name="bookmark" size={14} />
                Save search
              </button>
              <button type="button" className="chip chip-ghost" onClick={clear}>
                Clear all
              </button>
            </>
          )}

          {naming !== null && (
            <form
              className="chip-form"
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <input
                className="input input-small"
                aria-label="Name for this search"
                placeholder="Name it"
                autoFocus
                value={naming}
                onChange={(event) => setNaming(event.target.value)}
              />
              <button type="submit" className="button button-small" disabled={!naming.trim()}>
                Save
              </button>
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => setNaming(null)}
              >
                Cancel
              </button>
            </form>
          )}

          {!active && saved.length + recent.length > 0 && (
            <>
              {saved.map((entry) => (
                <span key={entry.id} className="chip chip-saved">
                  <button type="button" className="chip-main" onClick={() => apply(entry.query)}>
                    <Icon name="bookmark" size={14} />
                    {entry.name}
                  </button>
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={`Delete saved search ${entry.name}`}
                    onClick={() => void forget(entry.id)}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </span>
              ))}
              {recent.map((entry) => (
                <button key={entry} type="button" className="chip chip-ghost" onClick={() => apply(entry)}>
                  <Icon name="clock" size={14} />
                  {describe(entry)}
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {problem && <Notice tone="error">{problem}</Notice>}
      {list.error && <Notice tone="error">{list.error}</Notice>}

      {list.loading ? (
        <ul className="grid" aria-busy="true" aria-label="Loading">
          {Array.from({ length: 8 }, (_, index) => (
            <li key={index} className="card card-skeleton" />
          ))}
        </ul>
      ) : list.items.length === 0 ? (
        !active ? (
          <EmptyState
            icon="camera"
            title="Nothing here yet"
            action={
              <button type="button" className="button" onClick={onCapture}>
                <Icon name="plus" size={18} />
                Add your first item
              </button>
            }
          >
            Point a camera at the nearest thing you own.
          </EmptyState>
        ) : state.needs && !searching && filterCount === 1 ? (
          <EmptyState icon="check-circle" title="All caught up">
            Every item has a name.
          </EmptyState>
        ) : state.photos === "without" && !searching && filterCount === 1 ? (
          <EmptyState icon="check-circle" title="All caught up">
            Every item has a photo.
          </EmptyState>
        ) : state.location === null && !searching && filterCount === 1 ? (
          <EmptyState icon="check-circle" title="Everything has a home" />
        ) : (
          <EmptyState
            icon="search"
            title="No matches"
            action={
              <button type="button" className="button button-quiet" onClick={clear}>
                Clear search
              </button>
            }
          >
            Try fewer words, or loosen a filter.
          </EmptyState>
        )
      ) : (
        <>
          <ItemGrid
            items={list.items}
            dense={view === "list"}
            {...(selecting ? { selection: { picked, onToggle: toggle } } : {})}
            locationOf={locationOf}
            {...(stockLabels
              ? { stockOf: (item: ItemSummary) => stockLabels.get(item.id) ?? NOT_STOCK }
              : {})}
            onOpen={(id) => {
              remember();
              onOpen(id);
            }}
          />
          <LoadMore more={list.more} loading={list.loadingMore} onMore={list.showMore} />
          {selecting && (
            <BulkBar
              items={list.items}
              total={list.total}
              picked={picked}
              taxonomy={taxonomy}
              vocabulary={vocabulary}
              stock={user.activeKind === "stock"}
              onSelectAll={async () => {
                const all = await list.loadAll();
                if (all) setPicked(new Set(all.map((item) => item.id)));
              }}
              onClear={(keep) => setPicked(new Set(keep ?? []))}
              onLabels={() => onLabels([...picked])}
              onDone={() => {
                list.reload();
                setStockRead((count) => count + 1);
              }}
            />
          )}
        </>
      )}
    </>
  );
}
